import React, { useRef, useState, useEffect } from "react";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import { Button, Input, Space, Table } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import axios from "axios";
import "./cs_kpp_code.css";

interface KppDataType {
     id: string;
     kpp_name: string;
     kpp_code: string;
}

interface KppCodeModalProps { 
    onSelectKpp: (kppCode: string, kppName: string) => void; 
}

type DataIndex = keyof KppDataType;
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const KppCodeModal: React.FC<KppCodeModalProps> = ({ onSelectKpp }) => {
     const transformStyle = {
          transform: "scale(0.80)",
          transformOrigin: "top left",
          width: "125%",
     };

     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState("");
     const searchInput = useRef<InputRef>(null);
     const [kppData, setKppData] = useState<KppDataType[]>([]);
     
     const handleSearch = (
          selectedKeys: string[],
          confirm: FilterDropdownProps["confirm"],
          dataIndex: DataIndex,
     ) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText("");
     };

     const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<KppDataType> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${String(dataIndex).replace("_", " ").toUpperCase()}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters)}
                              size="small"
                              style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex);
                              }}>
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          onFilter: (value, record) =>
               record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
          filterDropdownProps: {
               onOpenChange(open) {
                    if (open) {
                         setTimeout(() => searchInput.current?.select(), 100);
                    }
               },
          },
          render: (text) =>
               searchedColumn === dataIndex ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     const columns: TableColumnsType<KppDataType> = [
          {
               title: "KPP NAME",
               dataIndex: "kpp_name",
               key: "kpp_name",
               width: "50%",
               ...getColumnSearchProps("kpp_name"),
               sorter: (a, b) => a.kpp_name.localeCompare(b.kpp_name),
               sortDirections: ["ascend", "descend"],
          },
          {
               title: "KPP KODE",
               dataIndex: "kpp_code",
               key: "kpp_code",
               width: "50%",
               ...getColumnSearchProps("kpp_code"),
               sorter: (a, b) => a.kpp_code.localeCompare(b.kpp_code),
               sortDirections: ["ascend", "descend"],
          },
     ];

     useEffect(() => {
          const fetchKppData = async () => {
               try {
                    const response = await axios.get<KppDataType[]>(`${backendUrl}/api/customer-master/kpp-data`);

                    setKppData(response.data.map((item) => ({ ...item, id: item.id.toString() })));
               } catch (error) {
                    console.error("Failed to fetch KPP data:", error);
               }
          };
          fetchKppData();
     }, []);

     return (
          <div id="iypr" style={transformStyle}>
               <div className="gjs-grid-row" id="ihvg">
                    <div className="gjs-grid-column" id="ibn5">
                         <Table<KppDataType>
                              columns={columns}
                              dataSource={kppData}
                              pagination={{ pageSize: 10 }}
                              rowKey="id"
                              onRow={(record) => {
                                   return {
                                        onClick: () => {
                                             console.log("Selected KPP:", record);
                                             onSelectKpp(record.kpp_code, record.kpp_name);
                                        },
                                   };
                              }}
                         />
                    </div>
               </div>
          </div>
     );
};

export default KppCodeModal;
